/**
 * KAN-845: экран "Начало работы" приложения менеджера. Здесь чистые правила:
 * какие шаги показать, какие из них сделаны, когда экран открывать сам, и
 * память "менеджер нажал Готово" (по id менеджера на этом устройстве).
 */
import type { PushView } from '@/hooks/usePushSubscription';
import type { InstallHintKind } from '@/lib/pwaInstall';
import { hasWorkHours } from '@/lib/workHours';
import type { WorkHours } from '@/types/manager';

export type OnboardingStepId = 'credentials' | 'install' | 'phone' | 'push' | 'telegram';

/**
 * KAN-846: шаг "Придумайте логин и пароль": needed - вход по ссылке без пароля
 * (обязательный шаг), done - задан на этом экране (галочка до ухода с экрана),
 * none - пароль уже был, шага нет.
 */
export type CredentialsState = 'needed' | 'done' | 'none';

export interface OnboardingStep {
  id: OnboardingStepId;
  done: boolean;
  /** Необязательный шаг не держит экран открытым ("можно позже"). */
  optional: boolean;
}

/** Статус Telegram менеджера; null: статус уведомлений неизвестен. */
export type TelegramLinkState = { linked: boolean; botConnected: boolean } | null;

export interface OnboardingInputs {
  /** KAN-846: нет у старых вызовов: шага нет. */
  credentials?: CredentialsState;
  installKind: InstallHintKind;
  phone: string;
  /** Когда можно звонить; без дней и времени шаг "Телефон" не сделан. */
  workHours?: WorkHours | null;
  pushView: PushView;
  /** null: статус уведомлений неизвестен (ошибка), шаг Telegram не показываем. */
  telegram: TelegramLinkState;
}

/** Состояние "переход на экран": так экран знает, что его открыли нарочно. */
export const ONBOARDING_NAV_STATE = { onboarding: true } as const;

export function isOnboardingNavState(state: unknown): boolean {
  return typeof state === 'object' && state !== null && (state as { onboarding?: unknown }).onboarding === true;
}

const STEP_IDS: readonly OnboardingStepId[] = ['credentials', 'install', 'phone', 'push', 'telegram'];

/** KAN-873: переход на экран к конкретному шагу (пометки строки настройки). */
export function onboardingStepNavState(step: OnboardingStepId): { onboarding: true; step: OnboardingStepId } {
  return { ...ONBOARDING_NAV_STATE, step };
}

/** KAN-873: шаг, к которому открыт экран; null, если открыт без шага. */
export function getOnboardingNavStep(state: unknown): OnboardingStepId | null {
  if (!isOnboardingNavState(state)) return null;
  const step = (state as { step?: unknown }).step;
  return STEP_IDS.find((id) => id === step) ?? null;
}

/**
 * KAN-868: правила шагов "push" и "telegram" отдельно, их же использует
 * плашка настройки (lib/managerSetupPending.ts).
 */
export function isPushDone(view: PushView): boolean {
  return view === 'on';
}

/** Браузер без Web Push: менеджер ничего не может сделать, плашка настройки про уведомления молчит. */
export function isPushUnavailable(view: PushView): boolean {
  return view === 'unsupported';
}

/** Шаг Telegram показываем, если есть бот для менеджеров или Telegram уже привязан. */
export function isTelegramStepShown(tg: TelegramLinkState): boolean {
  return !!tg && (tg.botConnected || tg.linked);
}

export function isTelegramDone(tg: TelegramLinkState): boolean {
  return !!tg && tg.linked;
}

/** Шаг Telegram показан и не сделан. */
export function isTelegramPending(tg: TelegramLinkState): boolean {
  return isTelegramStepShown(tg) && !isTelegramDone(tg);
}

export function buildOnboardingSteps(input: OnboardingInputs): OnboardingStep[] {
  const steps: OnboardingStep[] = [];
  const credentials = input.credentials ?? 'none';
  if (credentials !== 'none') {
    steps.push({ id: 'credentials', done: credentials === 'done', optional: false });
  }
  steps.push(
    { id: 'install', done: input.installKind === 'installed', optional: true },
    { id: 'phone', done: input.phone.trim() !== '' && hasWorkHours(input.workHours), optional: false },
    // KAN-872: уведомления по желанию, как Telegram: шаг не держит экран.
    { id: 'push', done: isPushDone(input.pushView), optional: true }
  );
  if (isTelegramStepShown(input.telegram)) {
    steps.push({ id: 'telegram', done: isTelegramDone(input.telegram), optional: true });
  }
  return steps;
}

/** Все обязательные шаги сделаны. */
export function requiredStepsDone(steps: readonly OnboardingStep[]): boolean {
  return steps.every((step) => step.optional || step.done);
}

const DISMISS_KEY_PREFIX = 'kan845-onboarding-dismissed:';
/** Запасная память на случай, когда localStorage недоступен (иначе экран открывался бы по кругу). */
const dismissedInMemory = new Set<string>();

export function isOnboardingDismissed(managerId: string): boolean {
  if (dismissedInMemory.has(managerId)) return true;
  try {
    return window.localStorage.getItem(DISMISS_KEY_PREFIX + managerId) === '1';
  } catch {
    return false;
  }
}

export function dismissOnboarding(managerId: string): void {
  dismissedInMemory.add(managerId);
  try {
    window.localStorage.setItem(DISMISS_KEY_PREFIX + managerId, '1');
  } catch {
    // нет хранилища (приватный режим): помним до перезагрузки
  }
}

/** Только для тестов. */
export function resetOnboardingMemoryForTests(): void {
  dismissedInMemory.clear();
}

export const ONBOARDING_TEXTS = {
  title: 'Начало работы',
  intro: 'Несколько шагов, чтобы не пропускать клиентов.',
  credentials: 'Придумайте логин и пароль',
  credentialsNote: 'Ссылка входа одноразовая. С логином и паролем вы войдёте снова на любом устройстве.',
  credentialsLoginPhone: 'Логин: ваш телефон',
  credentialsEmail: 'Почта (необязательно)',
  credentialsEmailRequired: 'Почта',
  credentialsEmailHint: 'Можно входить и по почте',
  credentialsNoPhone: 'Укажите почту: она будет логином. Или сначала сохраните телефон в шаге ниже.',
  credentialsPassword: 'Пароль',
  credentialsPasswordHint: 'Не короче 8 символов, латинские буквы и цифры',
  credentialsConfirm: 'Пароль ещё раз',
  credentialsSave: 'Сохранить логин и пароль',
  install: 'Добавьте приложение на главный экран',
  installNote: 'Можно позже. На iPhone без этого не будет уведомлений.',
  installDone: 'Приложение на главном экране',
  phone: 'Укажите свой телефон и когда вам можно звонить',
  phoneHoursMissing: 'Укажите дни и часы, когда вам можно звонить',
  phoneSave: 'Сохранить',
  push: 'Включите уведомления (по желанию)',
  pushIos: 'Сначала выполните шаг 1: добавьте приложение на главный экран и откройте его оттуда.',
  telegram: 'Подключите Telegram (по желанию)',
  optional: 'можно позже',
  done: 'Сделано',
  finish: 'Готово, перейти к клиентам',
  finishNeedsCredentials: 'Сначала задайте логин и пароль: без них вы не войдёте снова.',
} as const;
